/**
 * THE SUBSCRIPTION FORM (EXPERT-ROOMS.md §14b) — ONE renderer for an expert's form, used by the expert's preview, the
 * Subscribe popup and the profile editor. ``form`` = ``{title, intro, fields: [{id, label, help, type text|number|select|
 * boolean, required, options, default, example, optionLabels?, helper?}]}`` — the server sends it RESOLVED (a field bound
 * to an adapter shows the adapter's readable choices + its "how to find it" helper; EXPERT-ROOMS §14b, P2). A PROFILE = a name + the answers; they stay in the subscriber's
 * workspace. ``SubscribeDialog`` is the popup: open experts (no fields) never reach it — they subscribe in one click.
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/** PURE — a form that asks something (else the expert is open: one-click subscribe). */
export function formHasFields(form) {
  return !!(form && Array.isArray(form.fields) && form.fields.length);
}

/** PURE — the starting answers: a profile's values, else each field's default. */
export function initialValues(form, values = null) {
  const out = {};
  for (const f of (form?.fields || [])) {
    const v = values && values[f.id] !== undefined ? values[f.id] : f.default;
    out[f.id] = v === undefined || v === null ? (f.type === 'boolean' ? false : '') : v;
  }
  return out;
}

/** PURE — the required fields still empty → ``{id: 'required'}`` (the server checks everything else). */
export function missingRequired(form, values) {
  const out = {};
  for (const f of (form?.fields || [])) {
    if (f.required && f.type !== 'boolean' && (values?.[f.id] === undefined || String(values[f.id]).trim() === '')) out[f.id] = 'required';
  }
  return out;
}

/** PURE — a profile's answers routed to ONE platform adapter (its fixed values + the fields bound to it). */
export function adapterParams(form, values, adapterId) {
  const a = (form?.adapters || []).find((x) => x.id === adapterId);
  if (!a) return null;
  const out = { ...(a.fixed || {}) };
  for (const f of (form?.fields || [])) {
    const b = String(f.binds || '');
    if (b.startsWith(`adapter:${adapterId}.`)) {
      const v = values?.[f.id] !== undefined && values?.[f.id] !== '' ? values[f.id] : f.default;
      if (v !== undefined && v !== '') out[b.slice(`adapter:${adapterId}.`.length)] = v;
    }
  }
  return out;
}

const input = 'w-full rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-indigo-400 focus:outline-none disabled:bg-gray-50';

/** PURE — the fields a subscriber needn't look at (P2, 2026-11-03): each has a default, still holds it and has no
 *  error. The popup tucks them behind one "Using defaults: …" line — nothing is hidden silently: the line names every
 *  default in use and "Change" opens them; an error or a changed value always shows its field. */
export function tuckedFields(form, values, errors = {}) {
  return (form?.fields || []).filter((f) => f.default !== undefined && f.default !== null && f.default !== ''
    && !errors?.[f.id] && String(values?.[f.id] ?? f.default) === String(f.default));
}

/** PURE — how a default reads in that line: its choice's readable name, else the value. */
export function defaultLabel(f) {
  if (f.type === 'boolean') return f.default && String(f.default) !== 'false' ? 'yes' : 'no';
  return f.optionLabels?.[f.default] || String(f.default);
}

export function SubscriptionFormFields({ form, values, onChange, errors = {}, readOnly = false, testid = 'subscription-form',
  collapseDefaults = false }) {
  const set = (id, v) => onChange?.({ ...(values || {}), [id]: v });
  const [showAll, setShowAll] = useState(false);
  const tucked = collapseDefaults && !showAll ? tuckedFields(form, values, errors) : [];
  const hide = new Set(tucked.map((f) => f.id));
  const shown = (form?.fields || []).filter((f) => !hide.has(f.id));
  return (
    <div className="space-y-2.5" data-testid={testid}>
      {form?.intro ? <p className="text-xs text-gray-600">{form.intro}</p> : null}
      {tucked.length && shown.length ? (
        <p className="rounded-md bg-gray-50 px-2 py-1.5 text-[11px] text-gray-600" data-testid={`${testid}-defaults`}>
          Using defaults: {tucked.map((f) => `${f.label} — ${defaultLabel(f)}`).join(' · ')}{' '}
          <button type="button" className="font-medium text-indigo-600 hover:underline" onClick={() => setShowAll(true)}
            data-testid={`${testid}-defaults-change`}>Change</button>
        </p>
      ) : null}
      {(tucked.length && shown.length ? shown : (form?.fields || [])).map((f) => {
        const v = values?.[f.id];
        const err = errors?.[f.id];
        return (
          <label key={f.id} className="block text-xs" data-testid={`${testid}-field-${f.id}`}>
            {f.type === 'boolean' ? (
              <span className="flex items-center gap-2">
                <input type="checkbox" checked={!!v && String(v) !== 'false'} disabled={readOnly}
                  onChange={(e) => set(f.id, e.target.checked)} data-testid={`${testid}-input-${f.id}`} />
                <span className="font-medium text-gray-800">{f.label}</span>
              </span>
            ) : (
              <>
                <span className="font-medium text-gray-800">{f.label}{f.required ? <span className="text-rose-600"> *</span> : null}</span>
                {f.type === 'select' ? (
                  <select value={v ?? ''} disabled={readOnly} onChange={(e) => set(f.id, e.target.value)}
                    className={`${input} mt-1`} data-testid={`${testid}-input-${f.id}`}>
                    <option value="">Choose…</option>
                    {(f.options || []).map((o) => <option key={o} value={o}>{f.optionLabels?.[o] || o}</option>)}
                  </select>
                ) : (
                  <input type={f.type === 'number' ? 'number' : 'text'} value={v ?? ''} disabled={readOnly}
                    placeholder={f.example !== undefined ? String(f.example) : ''}
                    onChange={(e) => set(f.id, e.target.value)} className={`${input} mt-1`}
                    data-testid={`${testid}-input-${f.id}`} />
                )}
              </>
            )}
            {f.help ? <span className="mt-0.5 block text-[11px] text-gray-500">{f.help}</span> : null}
            {f.helper ? <span className="mt-0.5 block text-[11px] text-gray-400" data-testid={`${testid}-helper-${f.id}`}>{f.helper}</span> : null}
            {err ? <span className="mt-0.5 block text-[11px] text-rose-600" data-testid={`${testid}-error-${f.id}`}>{err}</span> : null}
          </label>
        );
      })}
    </div>
  );
}

/**
 * The popup — Subscribe (``mode='subscribe'``) or add / edit a profile (``mode='profile'``). ``onSubmit({name, values})``
 * resolves on success; a throw with ``err.fieldErrors`` marks those fields, else shows the message.
 */
export default function SubscribeDialog({ open, expertName = '', form, profile = null, mode = 'subscribe', onSubmit, onCancel }) {
  const [name, setName] = useState('');
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setName(profile?.name || (mode === 'subscribe' ? 'Default' : ''));
    setValues(initialValues(form, profile?.values));
    setErrors({}); setMsg(''); setBusy(false);
  }, [open, form, profile, mode]);
  const submit = async () => {
    const miss = missingRequired(form, values);
    if (!name.trim()) miss.__name = 'required';
    if (Object.keys(miss).length) { setErrors(miss); return; }
    setBusy(true); setErrors({}); setMsg('');
    try {
      await onSubmit?.({ ...(profile?.profileId ? { profileId: profile.profileId } : {}), name: name.trim(), values });
    } catch (e) {
      if (e?.fieldErrors) setErrors(e.fieldErrors);
      setMsg(e?.message || 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };
  const title = mode === 'subscribe' ? `Subscribe to ${expertName}` : (profile?.profileId ? 'Edit the profile' : 'Add a profile');
  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="subscribe-dialog"
          onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel?.(); }}>
          <motion.div className="flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl"
            initial={{ scale: 0.98, y: 6 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.98, y: 6 }}>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              <h3 className="text-base font-semibold text-gray-900">{title}</h3>
              {form?.title ? <p className="mt-0.5 text-xs font-medium text-gray-700">{form.title}</p> : null}
              <label className="mt-3 block text-xs">
                <span className="font-medium text-gray-800">Profile name<span className="text-rose-600"> *</span></span>
                <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} className={`${input} mt-1`}
                  data-testid="subscribe-dialog-name" />
                <span className="mt-0.5 block text-[11px] text-gray-500">
                  Rooms pick a profile when they plug the expert in. Your answers stay in this workspace.
                </span>
                {errors.__name ? <span className="mt-0.5 block text-[11px] text-rose-600">required</span> : null}
              </label>
              <div className="mt-3"><SubscriptionFormFields form={form} values={values} onChange={setValues} errors={errors}
                collapseDefaults /></div>
              {msg ? <p className="mt-2 text-[11px] text-rose-600" data-testid="subscribe-dialog-error">{msg}</p> : null}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-5 py-3">
              <button type="button" onClick={onCancel} disabled={busy} data-testid="subscribe-dialog-cancel"
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-50">Cancel</button>
              <button type="button" onClick={submit} disabled={busy} data-testid="subscribe-dialog-submit"
                className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                {busy ? 'Working…' : mode === 'subscribe' ? 'Subscribe' : 'Save'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
